/**
 * Brand colour tokens — runtime reader.
 *
 * The canonical definition of the coral/teal palette lives in the `@theme`
 * block of `src/index.css` as CSS custom properties (`--color-coral`, ...).
 * Anything rendered by CSS (Tailwind classes, shadcn vars, `var(--color-*)`)
 * therefore already follows whatever those properties currently resolve to,
 * including a runtime override that re-points them at a client's brand colours.
 *
 * Ant Design is the exception: its theme tokens are plain JS strings read at
 * render time, so it cannot consume a CSS variable directly. This module is the
 * bridge — it resolves the *computed* value of each custom property off
 * `:root`, so JS consumers see the same colour CSS does instead of keeping a
 * second hardcoded copy that silently drifts.
 *
 * The `BRAND_TOKEN_FALLBACKS` below exist only for environments where computed
 * custom properties are unavailable (SSR, jsdom/happy-dom unit tests, or the
 * first paint before the stylesheet is applied). They mirror `index.css` and
 * are the one place a literal hex is allowed outside that file.
 */

/** CSS custom property backing each brand token. */
export const BRAND_TOKEN_VARS = {
	coral: "--color-coral",
	coralHover: "--color-coral-hover",
	coralLight: "--color-coral-light",
	coralDark: "--color-coral-dark",
	teal: "--color-teal",
	tealHover: "--color-teal-hover",
	tealLight: "--color-teal-light",
	tealDark: "--color-teal-dark",
	warmBg: "--color-warm-bg",
} as const;

export type BrandTokenName = keyof typeof BRAND_TOKEN_VARS;

/**
 * Values mirroring the `@theme` block in `src/index.css`.
 * Used only when the computed custom property cannot be read.
 */
export const BRAND_TOKEN_FALLBACKS: Record<BrandTokenName, string> = {
	coral: "#0A76A8",
	coralHover: "#075E85",
	coralLight: "#E6F4FB",
	coralDark: "#074E6F",
	teal: "#6B7280",
	tealHover: "#4B5563",
	tealLight: "#F3F4F6",
	tealDark: "#374151",
	warmBg: "#FFFFFF",
};

export type BrandTokens = Record<BrandTokenName, string>;

const BRAND_TOKEN_NAMES = Object.keys(BRAND_TOKEN_VARS) as BrandTokenName[];

/**
 * Resolve one brand token from the document, falling back to the value baked
 * into `index.css` when the computed property is empty or unavailable.
 */
export function readBrandToken(name: BrandTokenName): string {
	const fallback = BRAND_TOKEN_FALLBACKS[name];

	if (typeof window === "undefined" || typeof document === "undefined") {
		return fallback;
	}

	try {
		const value = window
			.getComputedStyle(document.documentElement)
			.getPropertyValue(BRAND_TOKEN_VARS[name])
			.trim();
		return value || fallback;
	} catch {
		// happy-dom/jsdom can throw on getComputedStyle for detached roots.
		return fallback;
	}
}

/** Resolve every brand token in one pass. */
export function readBrandTokens(): BrandTokens {
	const resolved = {} as BrandTokens;
	for (const name of BRAND_TOKEN_NAMES) {
		resolved[name] = readBrandToken(name);
	}
	return resolved;
}
